import * as React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { uniq } from 'lodash';
import { AutoSizer } from 'react-virtualized';
import { isNullOrUndefined } from '../../store/storeFunctions';
import { registerHook, unregisterHook } from "../../store/clickWrapper";

import "./fixedRightTable.scss";

const side = {
    left: 1,
    right: 2
};

class FixedRightTable extends React.Component {
    initialState = { tooltippedCellKey: null, tooltipContent: null, scrollOffset: null, height: 0, dataCount: 0 };
    state = { ...this.initialState };
    cells = {};
    isDark = false;

    static propTypes = {
        leftData: PropTypes.array.isRequired,
        rightData: PropTypes.array.isRequired,
        leftHeader: PropTypes.array.isRequired,
        rightHeader: PropTypes.array.isRequired,
        leftColumnWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.func]).isRequired,
        rightColumnWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.func]).isRequired,
        rowHeight: PropTypes.number.isRequired,
        rowCount: PropTypes.number,
        maxWidth: PropTypes.number,
        center: PropTypes.bool,
        additionalHeight: PropTypes.func
    };

    static defaultProps = {
        leftHeader: [],
        rightHeader: [],
        leftData: [],
        rightData: [],
        leftColumnWidth: 100,
        rightColumnWidth: 100,
        rowHeight: 25,
        rowCount: 10,
        maxWidth: null,
        center: false,
        additionalHeight: null
    };

    componentDidMount() {
        this.hookId = registerHook(this, _ => this.setState({ ...this.initialState }));
        if (this.props.leftData.length !== this.props.rightData.length)
            throw new Error("Left and right data length must have equal number of rows!");
        if (this.props.leftHeader.length !== this.props.rightHeader.length)
            throw new Error("Left and right header must have equal number of rows!");
        if (uniq(this.props.leftData.map(d => d.length)).length > 1)
            throw new Error("Left data rows must have equal number of columns!");
        if (uniq(this.props.rightData.map(d => d.length)).length > 1)
            throw new Error("Right data rows must have equal number of columns!");
        if (uniq(this.props.leftHeader.map(d => d.length)).length > 1)
            throw new Error("Left header rows must have equal number of columns!");
        if (uniq(this.props.rightHeader.map(d => d.length)).length > 1)
            throw new Error("Right header rows must have equal number of columns!");
    }

    render() {
        if(!isNullOrUndefined(localStorage) && !isNullOrUndefined(localStorage.locales_theme) && localStorage.locales_theme === 'dark' )
            this.isDark = true;

        return (
            <AutoSizer className="fixed-table only-mobile" disableHeight >
                {({ width }) => (
                    <div className="fixed-table-inner" style={{ height: this.outerHeight(), display: "flex", flexDirection: "column" }}>
                        <div className={cn({ "fixed-right-table": true, "dark": this.isDark })}
                            style={{...this.calculateContainerStyle(width, this.props.maxWidth, this.props.center) }}
                        >
                            <div className="fixed-left" style={{ width: this.leftWidth(width), height: this.innerHeight() }}>
                                {this.props.leftHeader.map((l, i) => this.renderLeftRow(l, i, i === this.props.leftHeader.length - 1))}
                                {this.props.leftData.map((l, i) => this.renderLeftRow(l, i))}
                            </div>
                            <div className="fixed-right" style={{ minWidth: this.rightWidth(width), width: this.rightWidth(width), height: this.innerHeight() }}>
                                {this.props.rightHeader.map((r, i) => this.renderRightRow(r, i, width, i === this.props.rightHeader.length - 1))}
                                {this.props.rightData.map((r, i) => this.renderRightRow(r, i, width))}
                            </div>
                        </div>
                    </div>
                )}
            </AutoSizer>

        );
    }

    componentWillUnmount() {
        unregisterHook(this.hookId);
    }

    renderLeftRow = (row, key, isLast = null) => {
        return (
            <div key={key} className={cn({ "fixed-left-row": true, "last": isLast})} style={{ width: this.leftRowWidth() }}>
                {row.map((col, i) => {
                    const width = this.colWidth(i, side.left);
                    const left = this.leftPosition(i)
                    return <div key={i} className="fixed-cell"
                        style={{ height: this.props.rowHeight, maxHeight: this.props.rowHeight, width: width, minWidth: width, maxWidth: width, left: left }}
                    >
                        <div className="fixed-cell-content">
                            {col}
                        </div>
                    </div>
                })}
            </div>
        );
    }

    renderRightRow = (row, key, width, isLast = false) => {
        return (
            <div key={key} className={cn({ "fixed-right-row": true, "last": isLast})} style={{ width: this.rightWidth(width) }}>
                {row.map((col, i) => {
                    const colWidth = this.colWidth(i, side.right);
                    const left = this.leftPosition(i)
                    return <div key={i} className="fixed-cell" style={{ height: this.props.rowHeight, width: colWidth, minWidth: colWidth, maxWidth: colWidth, left: left }}>
                        <div className="fixed-cell-content">
                            {col}
                        </div>
                    </div>
                })}
            </div>
        );
    }

    calculateContainerStyle(width, maxWidth, shouldCenter) {
        if (isNullOrUndefined(maxWidth))
            return {};
        if (width <= maxWidth)
            return { width: `${width}px` };
        else {
            let style = { width: `${maxWidth}px`};
            if (!isNullOrUndefined(shouldCenter)) {
                style["position"] = "relative";
                style["left"] = `${Math.round((width - maxWidth) / 2)}px`;
            }
            return style;
        }
    }

    outerHeight = () => {
        const totalRows = this.props.leftData.length + this.props.leftHeader.length;
        const dataRows = !isNullOrUndefined(this.props.rowCount) && this.props.rowCount < totalRows ? this.props.rowCount : totalRows
        return this.props.rowHeight * dataRows;
    }

    innerHeight = (s) => {
        const totalRows = this.props.leftData.length + this.props.leftHeader.length;
        if (s === side.right)
            return this.props.rowHeight * totalRows;
        else {
            const additionalHeight = isNullOrUndefined(this.props.additionalHeight) ? 0 : this.props.additionalHeight();
            return this.props.rowHeight * totalRows + 6 + additionalHeight;
        }
    }

    colWidth = (index, s) => {
        return s === side.left
            ? (typeof this.props.leftColumnWidth === 'function' ? this.props.leftColumnWidth(index) : this.props.leftColumnWidth)
            : (typeof this.props.rightColumnWidth === 'function' ? this.props.rightColumnWidth(index) : this.props.rightColumnWidth);
    }

    rightWidth = (width) => {
        const maxWidth = width <= this.props.maxWidth ? width : this.props.maxWidth;
        let actualWidth = 0;
        for (var n in [...Array(this.props.rightColsCount).keys()])
            actualWidth += this.colWidth(n);
        return actualWidth <= maxWidth ? actualWidth : maxWidth;
    }

    leftWidth = (width) => width - this.rightWidth(width) - 6; // 6px for vertical scrollbar
    leftPosition = (index) => index === 0 ? 0 : this.sum([...Array(index).keys()].map(k => this.colWidth(k, side.left)));
    leftRowWidth = () => this.props.leftData.length === 0 ? 0 : this.sum([...Array(this.props.leftData[0].length).keys()].map(k => this.colWidth(k, side.left)));
    sum = (arr) => arr.reduce((a, b) => a + b, 0);
}

export default FixedRightTable;